import { FLAGS } from '@gozoki/feature-flags'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { SandboxBackButton } from '~/components/routes/sandbox-previsions/sandbox-back-button'
import { buttonVariants } from '~/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { DateRangeChip } from '../previsions/-date-range-chip'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import { AffectedTable, type AffectedRow } from './-affected-table'
import { BacktestPanel } from './-backtest-panel'
import { CorrectionModal } from './-correction-modal'
import { SelectionSummary } from './-selection-summary'
import { correctionSearchSchema, defaultCorrectionRange, exclusiveEnd } from './-search-schema'

const TABS: AppTab<'corrections' | 'backtest'>[] = [
  { value: 'corrections', label: 'Corrections' },
  { value: 'backtest', label: 'Backtest' },
]

export const Route = createFileRoute('/_authenticated/correction-ruptures/')({
  validateSearch: correctionSearchSchema,
  // Ressource propre depuis l'audit du 19/09 (TAX-M3) : un seul `read` ouvrait huit écrans de
  // prévision. L'écran est en lecture seule, l'édition n'a pas encore d'affordance.
  staticData: { permission: ['read', 'previsions.corrections.correction'] },
  component: CorrectionRupturesPage,
})
withRoutePermissions(Route)

function CorrectionRupturesPage() {
  const enabled = useFeatureFlag(FLAGS.salesUnitsCorrections)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [opened, setOpened] = useState<AffectedRow | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const range = {
    end: search.to ?? defaultCorrectionRange().end,
    start: search.from ?? defaultCorrectionRange().start,
  }
  const to = exclusiveEnd(range.end)
  const backtestStoreId = search.backtestStoreId ?? search.storeIds[0] ?? null

  const { data: stores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))
  const storeLabelOf = (id: number) => storeLabels.get(id) ?? `Boutique ${id}`

  const groupsQuery = useQuery(
    api.forecast.salesCorrectionCategoryDays.queryOptions(
      { query: { storeIds: search.storeIds, from: range.start, to } },
      { enabled: enabled && search.storeIds.length > 0 && search.tab === 'corrections' },
    ),
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Correction des ruptures</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <SandboxBackButton />
          <Title>Correction des ruptures</Title>
          <a
            href="/note-correction-ruptures.pdf"
            download
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <Download className="size-4" />
            Note méthodologique
          </a>
        </div>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-center gap-3">
          <PrevisionsStoreSelector
            selectedIds={search.storeIds}
            onApply={(storeIds) => setSearch({ storeIds })}
            placeholder="Sélectionner des boutiques"
            title="Boutiques à corriger"
          />
          <DateRangeChip
            direction="past"
            range={range}
            onChange={({ start, end }) => setSearch({ from: start, to: end })}
          />
        </div>

        <AppTabs
          className="mt-4"
          tabs={TABS}
          value={search.tab}
          onTabChange={(tab) => setSearch({ tab })}
        >
          <TabsContent value="corrections">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">Sélectionne au moins une boutique.</p>
            )}
            {search.storeIds.length > 0 && groupsQuery.isPending && (
              <p className="text-muted-foreground">Chargement…</p>
            )}
            {groupsQuery.isError && (
              <p className="text-amber-600">Erreur pendant le chargement. Réessaie.</p>
            )}
            {groupsQuery.data !== undefined && (
              <>
                <SelectionSummary totals={groupsQuery.data.totals} />
                <AffectedTable
                  key={`${range.start}|${range.end}|${search.storeIds.join(',')}`}
                  groups={groupsQuery.data.groups}
                  storeLabels={storeLabels}
                  onOpen={setOpened}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="backtest">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">Sélectionne au moins une boutique.</p>
            )}
            {backtestStoreId !== null && (
              <>
                {search.storeIds.length > 1 && (
                  <div className="mb-4 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Boutique</span>
                    <Select
                      value={String(backtestStoreId)}
                      onValueChange={(value) => setSearch({ backtestStoreId: Number(value) })}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {search.storeIds.map((storeId) => (
                          <SelectItem key={storeId} value={String(storeId)}>
                            {storeLabelOf(storeId)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <BacktestPanel storeId={backtestStoreId} from={range.start} to={to} />
              </>
            )}
          </TabsContent>
        </AppTabs>

        {opened && (
          <CorrectionModal
            row={opened}
            storeLabel={storeLabelOf(opened.storeId)}
            onClose={() => setOpened(null)}
          />
        )}
      </AppContainer>
    </>
  )
}
